import React from 'react';
import config from '../config';
import { useAuth } from '../auth/AuthContext';

const UploadContext = React.createContext(null);

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const recalcGroup = (group, uploads) => {
  if (!group) {
    return null;
  }
  const fileIds = group.fileIds;
  let loaded = 0;
  let total = 0;
  let completed = 0;
  let errors = 0;

  fileIds.forEach((id) => {
    const file = uploads[id];
    if (!file) {
      return;
    }
    const fileTotal = file.total ?? file.size ?? 0;
    const fileLoaded = Math.min(file.loaded ?? 0, fileTotal || Number.POSITIVE_INFINITY);
    total += fileTotal;
    loaded += fileLoaded;
    if (file.status === 'completed') {
      completed += 1;
    } else if (file.status === 'error') {
      errors += 1;
    }
  });

  const progress = total ? Math.min(100, Math.round((loaded / total) * 100)) : group.progress;
  let status = group.status;
  if (errors > 0) {
    status = 'error';
  } else if (completed === fileIds.length) {
    status = 'completed';
  } else if (loaded > 0) {
    status = 'uploading';
  }

  return {
    ...group,
    loadedBytes: loaded,
    totalBytes: total,
    progress,
    status,
    completedCount: completed,
    errorCount: errors,
  };
};

export const UploadProvider = ({ children }) => {
  const { accessToken, ensureValidToken } = useAuth();
  const [uploads, setUploads] = React.useState({});
  const [groups, setGroups] = React.useState({});
  const workerRef = React.useRef(null);

  React.useEffect(() => {
    const workerUrl = (() => {
      if (typeof window !== 'undefined') {
        return new URL('/workers/uploaderWorker.js', window.location.origin);
      }
      return new URL('./uploaderWorker.js', 'https://podboss.cchko.cc');
    })();

    const worker = new Worker(workerUrl, { type: 'module' });
    workerRef.current = worker;

    const handleMessage = (event) => {
      const message = event.data || {};
      const { type, id, groupId } = message;
      switch (type) {
        case 'UPLOAD_STARTED':
          setUploads((prev) => {
            if (!groupId) {
              return prev;
            }
            const next = { ...prev };
            const entry = next[id];
            if (entry) {
              next[id] = { ...entry, status: 'uploading' };
              setGroups((prevGroups) => {
                const group = recalcGroup(prevGroups[groupId], next);
                if (!group) {
                  return prevGroups;
                }
                return { ...prevGroups, [groupId]: group };
              });
            }
            return next;
          });
          break;
        case 'UPLOAD_PROGRESS':
          setUploads((prev) => {
            if (!groupId) {
              return prev;
            }
            const next = { ...prev };
            const entry = next[id];
            if (entry) {
              next[id] = {
                ...entry,
                status: 'uploading',
                progress: message.progress ?? entry.progress ?? 0,
                loaded: message.loaded ?? entry.loaded ?? 0,
                total: message.total ?? entry.total ?? entry.size ?? 0,
              };
              setGroups((prevGroups) => {
                const group = recalcGroup(prevGroups[groupId], next);
                if (!group) {
                  return prevGroups;
                }
                return { ...prevGroups, [groupId]: group };
              });
            }
            return next;
          });
          break;
        case 'UPLOAD_SUCCESS':
          setUploads((prev) => {
            if (!groupId) {
              return prev;
            }
            const next = { ...prev };
            const entry = next[id];
            if (entry) {
              const total = entry.total ?? entry.size ?? message.total ?? 0;
              next[id] = {
                ...entry,
                status: 'completed',
                progress: 100,
                loaded: total,
                total,
                image: message.image || entry.image || null,
              };
              setGroups((prevGroups) => {
                const group = recalcGroup(prevGroups[groupId], next);
                if (!group) {
                  return prevGroups;
                }
                return { ...prevGroups, [groupId]: group };
              });
            }
            return next;
          });
          break;
        case 'UPLOAD_ERROR':
          setUploads((prev) => {
            if (!groupId) {
              return prev;
            }
            const next = { ...prev };
            const entry = next[id];
            if (entry) {
              next[id] = {
                ...entry,
                status: 'error',
                error: message.error || entry.error,
              };
              setGroups((prevGroups) => {
                const group = recalcGroup(prevGroups[groupId], next);
                if (!group) {
                  return prevGroups;
                }
                return { ...prevGroups, [groupId]: group };
              });
            }
            return next;
          });
          break;
        case 'QUEUE_EMPTY':
          break;
        default:
          break;
      }
    };

    worker.addEventListener('message', handleMessage);
    worker.postMessage({ type: 'SET_OPTIONS', baseUrl: config.apiBaseUrl });

    return () => {
      worker.removeEventListener('message', handleMessage);
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'SET_AUTH_TOKEN', token: accessToken || null });
    }
  }, [accessToken]);

  const queueFiles = React.useCallback(
    (fileList, uploadOptions = null) => {
      const files = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
      if (!files.length) {
        return null;
      }

      const groupId = createId();
      const groupName = files.length === 1 ? files[0].name : `${files[0].name} (+${files.length - 1})`;
      let optionsJSON = '';
      if (uploadOptions && typeof uploadOptions === 'object') {
        try {
          optionsJSON = JSON.stringify(uploadOptions);
        } catch (err) {
          console.error('failed to serialize upload options', err);
          optionsJSON = '';
        }
      }

      const prepared = files.map((file) => ({
        id: createId(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        groupId,
        options: optionsJSON,
      }));

      const fileIds = prepared.map((entry) => entry.id);

      setUploads((prev) => {
        const next = { ...prev };
        prepared.forEach((entry) => {
          next[entry.id] = {
            id: entry.id,
            name: entry.name,
            size: entry.size,
            type: entry.type,
            groupId: entry.groupId,
            status: 'queued',
            progress: 0,
            loaded: 0,
            total: entry.size,
            error: null,
          };
        });
        return next;
      });

      setGroups((prev) => ({
        ...prev,
        [groupId]: {
          id: groupId,
          name: groupName,
          fileIds,
          loadedBytes: 0,
          totalBytes: prepared.reduce((sum, entry) => sum + entry.size, 0),
          progress: 0,
          status: 'queued',
          completedCount: 0,
          errorCount: 0,
          createdAt: Date.now(),
        },
      }));

      const sendToWorker = async () => {
        let token = accessToken || null;
        if (ensureValidToken) {
          try {
            token = await ensureValidToken();
          } catch (err) {
            console.error('Unable to refresh auth token for upload', err);
            setUploads((prev) => {
              const next = { ...prev };
              fileIds.forEach((id) => {
                if (next[id]) {
                  next[id] = { ...next[id], status: 'error', error: 'Authentication required' };
                }
              });
              return next;
            });
            setGroups((prevGroups) => {
              const current = prevGroups[groupId];
              if (!current) {
                return prevGroups;
              }
              return { ...prevGroups, [groupId]: { ...current, status: 'error', progress: 0 } };
            });
            return;
          }
        }

        if (workerRef.current) {
          workerRef.current.postMessage({ type: 'SET_AUTH_TOKEN', token: token || null });
          workerRef.current.postMessage({ type: 'UPLOAD_FILES', files: prepared });
        }
      };

      sendToWorker().catch((err) => {
        console.error('Unexpected upload error', err);
      });

      return { groupId };
    },
    [accessToken, ensureValidToken],
  );

  const overallProgress = React.useMemo(() => {
    const activeGroups = Object.values(groups).filter((group) => group.status !== 'completed' && group.status !== 'error');
    if (!activeGroups.length) {
      return null;
    }
    const loaded = activeGroups.reduce((sum, group) => sum + (group.loadedBytes || 0), 0);
    const total = activeGroups.reduce((sum, group) => sum + (group.totalBytes || 0), 0);
    if (!total) {
      return null;
    }
    return Math.min(100, Math.round((loaded / total) * 100));
  }, [groups]);

  const value = React.useMemo(
    () => ({
      uploads,
      groups,
      queueFiles,
      overallProgress,
      hasActiveUploads: overallProgress !== null,
    }),
    [uploads, groups, queueFiles, overallProgress],
  );

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
};

export const useUploadManager = () => {
  const ctx = React.useContext(UploadContext);
  if (!ctx) {
    throw new Error('useUploadManager must be used within an UploadProvider');
  }
  return ctx;
};
